import { Box, Grid, Typography, Stack } from "@mui/material"
import { UserMeetings } from "../components/upcomingOrders/meetings/UserMeetings"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { UserPurchases } from "../components/upcomingOrders/purchases/UserPurchases"
import { CanteenInfo } from "../components/canteen/CanteenInfo"
import { OffersSection } from "../components/canteen/offers/OffersSection"
import { NewsSection } from "../components/canteen/news/NewsSection"
import { TodayMenuSection } from "../components/canteen/menu/TodayMenuSection"
import { useState } from "react"
import { RoomFinderDrawer } from "../components/roomFinder/RoomFinderDrawer"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppDispatch, useAppSelector } from "../../../../store/storeHooks"
import { MatchEnvironment } from "../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../environment/envTypes"
import { selectUserId } from "../../../../login/loginSelectors"
import { ModuleName } from "../../../../users/userTypes"
import { CreateOrderType } from "../../../../../shared/components/createOrder/helpers"
import { FinalSuccessDialogContent } from "../../../../../shared/components/createOrder/components/finalSuccessDialog/FinalSuccessDialogContent"
import { getUserDetails, UserQuery } from "../../../../users/userApi"
import { FindRoom } from "../components/canteen/FindRoom"
import { makeStyles } from "tss-react/mui"
import { useOrderDialogUrlState } from "../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"

const useStyles = makeStyles()(() => ({
    container: {
        paddingTop: "3rem",
    },
    comingSoonOuter: {
        flexWrap: "nowrap",
        height: "100%",
        paddingTop: "6rem",
        justifyContent: "center",
        alignItems: "center",
    },
    comingSoonInner: {
        flexWrap: "nowrap",
        gap: "1.5rem",
        width: "60%",
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
}))

export function UserReportsHome() {
    const { classes } = useStyles()
    const userId = useAppSelector(selectUserId)
    const [isRoomFinderOpen, setIsRoomFinderOpen] = useState(false)
    const { openDialog } = useOrderDialogUrlState()

    const dispatch = useAppDispatch()
    const [finalSuccessDialog, setFinalSuccessDialog] = useState(false)
    const [orderId, setOrderId] = useState<number>()

    const { data: userDetails } = useApiQuery(getUserDetails, {
        queryName: UserQuery.USER_DETAILS,
        dependencies: {
            userId: userId ?? 0,
        },
        enabled: Boolean(userId),
    })

    const activeModules = new Set(userDetails?.user?.modules?.filter((m) => m.isActive)?.map((m) => m.name))

    const handleOpenSuccessDialog = (orderId: number) => {
        setOrderId(orderId)
        setFinalSuccessDialog(true)
    }

    return (
        <>
            <MatchEnvironment environments={[EnvironmentType.Prod, EnvironmentType.QA]}>
                <Stack className={classes.comingSoonOuter}>
                    <Stack direction="column" className={classes.comingSoonInner}>
                        <Typography variant="h4">Velkommen til Facilitynet 2</Typography>
                        <Typography variant="h6">Du er logget ind på Facilitynet 2, som snart lanceres.</Typography>
                        <Typography variant="h6">
                            Du skal logge ind på <a href="https://bestil-nu.net">https://bestil-nu.net</a> for at
                            planlægge møder og bestille kantineydelser indtil vi åbner for Facilitynet 2.
                        </Typography>
                    </Stack>
                </Stack>
            </MatchEnvironment>
            <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}>
                <Box className={classes.container}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={{ md: "3rem", xl: "5rem" }}>
                        <Grid item xs={12} md={6}>
                            <Stack direction="column" spacing="1rem">
                                <LocalizedStrict id="user-home-upcoming-orders">
                                    <Typography variant="h2" lineHeight={0.8} pb=".5rem" fontWeight={500}>
                                        Upcoming orders
                                    </Typography>
                                </LocalizedStrict>
                                <Grid container spacing={2} xs={12} md={12}>
                                    <UserMeetings />
                                    <UserPurchases />
                                </Grid>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <CanteenInfo />
                            <MatchEnvironment
                                environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo]}
                            >
                                {activeModules.has(ModuleName.BOOKING) && (
                                    <FindRoom
                                        onOpenRoomFinder={() => {
                                            setIsRoomFinderOpen(true)
                                        }}
                                    />
                                )}
                            </MatchEnvironment>
                            <OffersSection />
                            <TodayMenuSection />
                            <NewsSection />
                        </Grid>
                    </Stack>

                    <RoomFinderDrawer
                        open={isRoomFinderOpen}
                        openSuccessDialog={handleOpenSuccessDialog}
                        onClose={() => setIsRoomFinderOpen(false)}
                    />

                    {finalSuccessDialog && (
                        <FinalSuccessDialogContent
                            open={finalSuccessDialog}
                            onClose={() => setFinalSuccessDialog(false)}
                            handleNewOrder={() => {
                                setIsRoomFinderOpen(true)
                                setFinalSuccessDialog(false)
                            }}
                            handleAddMeetingService={() => {
                                openDialog({
                                    orderId: orderId,
                                    mode: "edit",
                                    activeOrderTab: CreateOrderType.MEETING_SERVICE,
                                })
                                setFinalSuccessDialog(false)
                            }}
                            handleFinish={() => setFinalSuccessDialog(false)}
                        />
                    )}
                </Box>
            </MatchEnvironment>
        </>
    )
}
